import React, { useEffect, useState } from "react";
import { useAuth, useSignUp } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { ReactComponent as GoogleIcon } from "../static/Icons/google.svg";
import { ReactComponent as GithubIcon } from "../static/Icons/github.svg";
import { ReactComponent as MicrosoftIcon } from "../static/Icons/microsoft.svg";
import { ReactComponent as MCPlogo } from "../static/Icons/mcp-logo.svg";
import ConfirmationCode from "./ConfirmationCode";

const SignupPage = () => {
  const navigate = useNavigate();
  const { signUp, setActive, isLoaded } = useSignUp();
  const { getToken, isSignedIn } = useAuth();
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  // const [verificationCode, setVerificationCode] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // const handleVerificationChange = (e) => {
  //   setVerificationCode(e.target.value);
  // };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if user is signed in after OAuth redirect
      if (isSignedIn && signUp) {
        try {
          const token = await getToken({ template: "mcp-auth" });
          localStorage.setItem("mcp_token", token);
          window.location.href = "/dashboard";
        } catch (err) {
          console.error("Token error:", err);
        }
      }
    };

    if (isLoaded) {
      handleOAuthCallback();
    }
  }, [isSignedIn, isLoaded, getToken, signUp]);

  const handleGoogleSignup = async (e) => {
    e.preventDefault();

    try {
      const baseUrl = window.location.origin;
      await signUp.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: `${baseUrl}/dashboard`,
        redirectUrlComplete: `${baseUrl}/dashboard`,
      });
    } catch (err) {
      console.error("Google sign-up error:", err);
      setError("Google sign-up failed. Please try again.");
    }
  };

  const handleGitHubSignup = async (e) => {
    e.preventDefault();
    try {
      const baseUrl = window.location.origin;
      await signUp.authenticateWithRedirect({
        strategy: "oauth_github",
        redirectUrl: `${baseUrl}/dashboard`,
        redirectUrlComplete: `${baseUrl}/dashboard`,
      });
    } catch (err) {
      console.error("GitHub sign-in error:", err);
      setError("GitHub sign-in failed. Please try again.");
    }
  };

  const handleMicrosoftSignup = async (e) => {
    e.preventDefault();
    try {
      const baseUrl = window.location.origin;
      await signUp.authenticateWithRedirect({
        strategy: "oauth_microsoft",
        redirectUrl: `${baseUrl}/dashboard`,
        redirectUrlComplete: `${baseUrl}/dashboard`,
      });
    } catch (err) {
      console.error("Microsoft sign-in error:", err);
      setError("Microsoft sign-in failed. Please try again.");
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Basic validation
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      setLoading(false);
      return;
    }

    try {
      // Attempt to create the user
      const signUpResult = await signUp.create({
        // username: form.username,
        emailAddress: form.email,
        password: form.password,
      });

      // Handle different signup states
      if (signUpResult.status === "complete") {
        // User created and signed in
        await setActive({ session: signUpResult.createdSessionId });
        navigate("/signin");
      } else if (signUpResult.status === "missing_requirements") {
        // Handle missing fields
        console.error("Missing requirements:", signUpResult);
        if (
          signUpResult.missingFields &&
          signUpResult.missingFields.length > 0
        ) {
          setError(
            `Missing required fields: ${signUpResult.missingFields.join(", ")}`
          );
        } else if (
          signUpResult.unverifiedFields &&
          signUpResult.unverifiedFields.includes("email_address")
        ) {
          // If the email needs verification but the normal flow didn't catch it
          await signUp.prepareEmailAddressVerification({
            strategy: "email_code",
          });
          setShowVerification(true);
        } else {
          setError("Could not complete signup. Please check all fields.");
        }
      } else if (signUpResult.status === "unverified") {
        // User created but needs verification
        await signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });
        setShowVerification(true);
      } else {
        setError("Sign-up failed with status: " + signUpResult.status);
      }
    } catch (err) {
      console.error("Signup error:", err);
      setError(err.errors?.[0]?.message || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // const handleVerifyEmail = async (e) => {
  //   e.preventDefault();
  //   setError("");
  //   setLoading(true);

  //   try {
  //     // Attempt to verify the email address
  //     const result = await signUp.attemptEmailAddressVerification({
  //       code: verificationCode,
  //     });

  //     if (result.status === "complete") {
  //       // User is verified and signed in
  //       await setActive({ session: result.createdSessionId });
  //       navigate("/login");
  //     } else {
  //       setError("Verification failed with status: " + result.status);
  //     }
  //   } catch (err) {
  //     console.error("Verification error:", err);
  //     setError(
  //       err.errors?.[0]?.message || "Verification failed. Please try again."
  //     );
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Return loading state if Clerk isn't loaded yet
  if (!isLoaded) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        overflowY: "auto",
        bgcolor: "#2B2B2B",
        color: "#FFFFFF",
        display: "flex",
        alignItems: showVerification ? "center" : "start",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "500px", p: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <Box
            component={MCPlogo}
            sx={{
              width: 250,
              height: 60,
            }}
          />
        </Box>
        <Box sx={{ border: "1px solid #BCBCBC", borderRadius: 2, p: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            {showVerification ? "Confirmation Code" : "Create an account"}
          </Typography>

          {showVerification ? (
            // Verification form
            <ConfirmationCode />
          ) : (
            // <Box component="form" onSubmit={handleVerifyEmail} sx={{ mt: 2 }}>
            //   <Typography paragraph>Confirmation Code</Typography>
            //   <TextField
            //     margin="normal"
            //     fullWidth
            //     name="verificationCode"
            //     label="Verification Code"
            //     value={verificationCode}
            //     onChange={handleVerificationChange}
            //     required
            //     sx={{ mb: 2 }}
            //     size="small"
            //   />
            //   {error && (
            //     <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            //       {error}
            //     </Alert>
            //   )}
            //   <Button
            //     type="submit"
            //     fullWidth
            //     variant="contained"
            //     sx={{
            //       mt: 3,
            //       py: 1.5,
            //       borderRadius: 9999,
            //       bgcolor: "#c9fa3b",
            //       color: "#000",
            //       "&:hover": {
            //         bgcolor: "#b0ff12",
            //       },
            //       textTransform: "capitalize",
            //       fontWeight: 600,
            //     }}
            //     disabled={loading}
            //   >
            //     {loading ? <CircularProgress size={24} /> : "Sign Up"}
            //   </Button>
            // </Box>
            // Signup form
            <>
              {/* Social Media Icons */}
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    textAlign: "center",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    cursor: "pointer",
                  }}
                  onClick={handleGoogleSignup}
                >
                  <GoogleIcon />
                </Box>

                <Box
                  sx={{
                    textAlign: "center",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    cursor: "pointer",
                  }}
                  onClick={handleGitHubSignup}
                >
                  <GithubIcon />
                </Box>

                <Box
                  sx={{
                    textAlign: "center",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    cursor: "pointer",
                  }}
                  onClick={handleMicrosoftSignup}
                >
                  <MicrosoftIcon />
                </Box>
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  my: 3,
                }}
              >
                <Divider sx={{ flexGrow: 1, bgcolor: "#fff" }} />
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ px: 2, color: "#fff" }}
                >
                  Or
                </Typography>
                <Divider sx={{ flexGrow: 1, bgcolor: "#fff" }} />
              </Box>

              <Box component="form" onSubmit={handleSignup}>
                {/* <Typography variant="body1" sx={{ mb: 1 }}>
                  Name
                </Typography>
                <TextField
                  fullWidth
                  name="username"
                  placeholder="Enter your username"
                  value={form.username}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      color: "#BCBCBC", // Text color inside the input
                      "& fieldset": {
                        borderColor: "#BCBCBC", // Default border color
                      },
                      "&:hover fieldset": {
                        borderColor: "#BCBCBC", // Border color on hover
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#BCBCBC", // Border color when focused
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#BCBCBC", // Label color
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#BCBCBC", // Label color when focused
                    },
                  }}
                  size="small"
                /> */}

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={form.email}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      color: "#BCBCBC", // Text color inside the input
                      "& fieldset": {
                        borderColor: "#BCBCBC", // Default border color
                      },
                      "&:hover fieldset": {
                        borderColor: "#BCBCBC", // Border color on hover
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#BCBCBC", // Border color when focused
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#BCBCBC", // Label color
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#BCBCBC", // Label color when focused
                    },
                  }}
                  size="small"
                />

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create your password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          sx={{ color: "#BCBCBC" }}
                        >
                          {showPassword ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      color: "#BCBCBC", // Text color inside the input
                      "& fieldset": {
                        borderColor: "#BCBCBC", // Default border color
                      },
                      "&:hover fieldset": {
                        borderColor: "#BCBCBC", // Border color on hover
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#BCBCBC", // Border color when focused
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#BCBCBC", // Label color
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#BCBCBC", // Label color when focused
                    },
                  }}
                />

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Confirm Password
                </Typography>
                <TextField
                  fullWidth
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Enter confirm password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                          sx={{ color: "#BCBCBC" }}
                        >
                          {showConfirmPassword ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      color: "#BCBCBC", // Text color inside the input
                      "& fieldset": {
                        borderColor: "#BCBCBC", // Default border color
                      },
                      "&:hover fieldset": {
                        borderColor: "#BCBCBC", // Border color on hover
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#BCBCBC", // Border color when focused
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#BCBCBC", // Label color
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#BCBCBC", // Label color when focused
                    },
                  }}
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: 9999,
                    bgcolor: "#CFFF04",
                    color: "#000",
                    boxShadow: "0px 4px 10.2px 3px #CFFF045C",
                    textTransform: "capitalize",
                    "&:hover": {
                      bgcolor: "#b0ff12",
                    },
                    fontWeight: 600,
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Create an account"
                  )}
                </Button>
              </Box>
            </>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2">
              Already have an account?{" "}
              <Link
                href={"/signin"}
                mode="modal"
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  fontWeight: "medium",
                }}
              >
                Login
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignupPage;
